import * as React from 'react';
import {
  MasonryEngineNode,
  MasonryEngineNodeAction,
  MasonryEngineNodeRendererProps,
  useMasonryEngineActionEmitter,
} from '@leagueplatform/masonry-engine';
import {
  Box,
  StackLayout,
  UtilityText,
  Button,
  GDSIconSource,
  GDSHeadingLevel,
  Divider,
  ParagraphText,
  TextAction,
  Image,
} from '@leagueplatform/genesis-core';
import {
  isLinkAction,
  MasonryDeeplinkAction,
} from '@leagueplatform/masonry-renderers';
import { useUniqueId } from '@leagueplatform/web-common';
import { InternalOrExternalLink as Link } from '@leagueplatform/web-common-components';
import { SectionHeaderWithIcon } from '#components/section-header-with-icon/section-header-with-icon.component.tsx';
import { useIntl } from '@leagueplatform/locales';
import {
  buttonIndicatorMap,
  ButtonIndicatorType,
} from '#constants/indicator-map.constants.ts';

export const NODE_TYPE_ADDITIONAL_BENEFITS_WIDGET = 'additionalBenefitsWidget';

interface Icon {
  icon?: GDSIconSource | string;
  description?: string;
}

interface ButtonType {
  label: string;
  indicator?: ButtonIndicatorType;
}

type AdditionalBenefitsWidgetProps = {
  sponsorLogo?: {
    description?: string;
    height?: string;
    url: string;
    width?: string;
  };
  headingLeadingIcon?: Icon;
  heading: string;
  headingLevel?: GDSHeadingLevel;
  description: string;
  subtext?: string;
  isCTALabelGeneric: boolean;
  hasEndDivider?: boolean;
  button?: ButtonType;
  moreDetails?: string;
};

export type AdditionalBenefitsWidgetNodeActions = {
  onClick?: MasonryEngineNodeAction;
  onLoad?: MasonryEngineNodeAction;
  onMoreDetailsClick?: MasonryEngineNodeAction;
};

type AdditionalBenefitsWidgetNode = MasonryEngineNode<
  typeof NODE_TYPE_ADDITIONAL_BENEFITS_WIDGET,
  AdditionalBenefitsWidgetProps,
  AdditionalBenefitsWidgetNodeActions,
  {}
>;

export type AdditionalBenefitsWidgetRendererProps =
  MasonryEngineNodeRendererProps<AdditionalBenefitsWidgetNode>;

export function AdditionalBenefitsWidgetRenderer({
  sponsorLogo,
  headingLeadingIcon,
  heading,
  headingLevel = '2',
  description,
  subtext,
  isCTALabelGeneric,
  hasEndDivider = false,
  button,
  onClick,
  moreDetails,
  onMoreDetailsClick,
}: AdditionalBenefitsWidgetRendererProps) {
  const { $tifelse } = useIntl();
  const invokeAction = useMasonryEngineActionEmitter();
  const [moreDetailsVisible, setMoreDetailsVisible] = React.useState(false);
  const onClickDeeplinkAction = onClick as MasonryDeeplinkAction | undefined;
  const uniqueId = useUniqueId({
    prefix: 'additional-benefits-widget',
  });
  // IDs for accessibility
  const headingId = `${uniqueId}-heading`;

  const isLink = React.useMemo(
    () => isLinkAction(onClickDeeplinkAction),
    [onClickDeeplinkAction],
  );

  const href = onClickDeeplinkAction?.payload.url;

  // Handle click events
  const handleOnClick = React.useCallback(
    (event: React.SyntheticEvent) => {
      if (onClick) {
        invokeAction({
          event,
          ...onClick,
        });
      }
    },
    [invokeAction, onClick],
  );

  const handleMoreDetailsClick = React.useCallback(
    (event: React.SyntheticEvent) => {
      setMoreDetailsVisible((prev) => !prev);
      if (onMoreDetailsClick) {
        invokeAction({
          event,
          ...onMoreDetailsClick,
        });
      }
    },
    [invokeAction, onMoreDetailsClick],
  );

  return (
    <StackLayout as="section" aria-labelledby={headingId} spacing="$oneAndHalf">
      {sponsorLogo && (
        <Box>
          <Image
            src={sponsorLogo.url}
            alt={sponsorLogo.description ?? ''}
            width={sponsorLogo.width}
            height={sponsorLogo.height}
            loading="lazy"
            css={{ maxWidth: '100%' }}
          />
        </Box>
      )}

      <StackLayout spacing="$oneAndHalf">
        <Box css={{ display: 'flex', alignItems: 'center' }}>
          <SectionHeaderWithIcon
            heading={heading}
            headingLevel={headingLevel}
            icon={headingLeadingIcon}
            id={headingId}
          />
        </Box>

        {description && (
          <StackLayout spacing="$oneAndHalf">
            <Box>
              <ParagraphText>{description}</ParagraphText>
              {moreDetails && (
                <ParagraphText hidden={!moreDetailsVisible}>
                  {moreDetails}
                </ParagraphText>
              )}
            </Box>
            {moreDetails && (
              <TextAction
                aria-expanded={moreDetailsVisible}
                aria-labelledby={`${headingId}-more-details ${headingId}`}
                as="button"
                color="subdued"
                id={`${headingId}-more-details`}
                onClick={handleMoreDetailsClick}
                icon={
                  moreDetailsVisible
                    ? 'interfaceChevronUp'
                    : 'interfaceChevronDown'
                }
              >
                {$tifelse(
                  moreDetailsVisible,
                  'BENEFITS_MORE_DETAILS_EXPANDED_LABEL',
                  'BENEFITS_MORE_DETAILS_COLLAPSED_LABEL',
                )}
              </TextAction>
            )}
          </StackLayout>
        )}

        {subtext && (
          <UtilityText size="sm" color="subdued">
            {subtext}
          </UtilityText>
        )}

        {button && (
          <Box>
            <Button
              size="medium"
              priority="secondary"
              onClick={handleOnClick}
              indicator={
                button.indicator
                  ? buttonIndicatorMap[button.indicator]
                  : undefined
              }
              id={isCTALabelGeneric ? `${headingId}-cta` : undefined}
              aria-labelledby={
                isCTALabelGeneric ? `${headingId}-cta ${headingId}` : undefined
              }
              // eslint-disable-next-line react/jsx-props-no-spreading
              {...(isLink && {
                as: Link,
                href,
              })}
            >
              {button.label}
            </Button>
          </Box>
        )}
      </StackLayout>

      {hasEndDivider && <Divider />}
    </StackLayout>
  );
}
